import { Button } from '@/components/ds2'
import type { ListPage } from './cardResults'

/** What a list card lists, named by the card that answered its rows. */
export type ListSubject = 'invoices' | 'loads'

const count = (value: number): string => value.toLocaleString('en-US')

/** How much of the list the table holds: "20 of 45" (a later page names its range), or where a named read was cut. */
const pageWords = (page: ListPage): string => {
    if (page.truncated) return `List cut at ${page.shown}`
    const range = page.first > 1 ? `${page.first}–${page.first + page.shown - 1}` : `${page.shown}`
    return page.total === undefined ? `Showing ${range}` : `Showing ${range} of ${count(page.total)}`
}

/** Whether the list holds more than the rows shown: a total above the shown rows, a next page, or a read cut short. */
const hasMoreRows = (page: ListPage): boolean => page.truncated || page.nextPage !== undefined || (page.total !== undefined && page.total > page.shown)

/** How many the next "Show N more" adds: the page size, or what is left when that is less. */
const nextCountOf = (page: ListPage): number => {
    const size = page.limit ?? page.shown
    return page.total === undefined ? size : Math.max(0, Math.min(size, page.total - page.shown))
}

/** The list's position for the control's label: "20 of 905", or the rows shown when no total is known. */
const positionOf = (page: ListPage | undefined): string =>
    !page ? '' : ` · ${page.total === undefined ? count(page.shown) : `${count(page.shown)} of ${count(page.total)}`}`

interface PageFooterProps {
    page?: ListPage
    /** Reads the next server page into the same answer block. Absent when the list cannot be paged in place. */
    onLoadMore?: () => void
    pending?: boolean
    error?: string
    /** Rows the answer already holds but does not draw: the one control reveals them first. */
    localHidden?: number
    onRevealLocal?: () => void
}

/**
 * The list's one control, at its bottom: "Show 20 more · 20 of 905". While rows the answer already holds are undrawn it reveals them; then it
 * reads the next server page into this same block. Nothing is sent to the chat. With no control, only how much of the list is shown.
 */
export const PageFooter = ({ page, onLoadMore, pending = false, error, localHidden = 0, onRevealLocal }: PageFooterProps) => {
    const localControl = localHidden > 0 && !!onRevealLocal
    const serverControl = !!page && page.nextPage !== undefined && !!onLoadMore
    if (!localControl && (!page || !hasMoreRows(page))) return null
    return (
        <div className="flex flex-col gap-1">
            {localControl ? (
                <div className="flex">
                    <Button size="sm" variant="ghost" onClick={onRevealLocal}>
                        {`Show ${count(localHidden)} more${positionOf(page)}`}
                    </Button>
                </div>
            ) : serverControl && page ? (
                <div className="flex">
                    <Button size="sm" variant="ghost" disabled={pending} onClick={onLoadMore}>
                        {pending ? 'Loading…' : `Show ${count(nextCountOf(page))} more${positionOf(page)}`}
                    </Button>
                </div>
            ) : (
                page && <span className="text-xs text-foreground-muted">{pageWords(page)}</span>
            )}
            {error && (
                <p role="alert" className="text-xs text-danger">
                    {error}
                </p>
            )}
        </div>
    )
}
